<template>
  <v-container fluid class="pa-0">
    <!-- Header section with gradient background -->
    <v-sheet
        color="primary"
        class="py-8 mb-8"
        rounded="0"
        elevation="1"
    >
      <v-row justify="center" class="text-center mt-2">
        <v-icon icon="mdi-account" size="56" color="white" class="mb-4"></v-icon>
        <h1 class="text-h3 font-weight-light text-white">{{ t('personagrid.personas') }}</h1>
      </v-row>
      <v-row justify="center" class="text-center">
        <div class="text-subtitle-1 text-white mt-2">{{ t('personagrid.viewDetailedInfoAboutAllPersonas') }}</div>
      </v-row>
    </v-sheet>

    <div class="d-flex justify-space-between align-center mb-4 mx-8">
      <v-select
          class="domain-select"
          :label="$t('generic.domain')"
          :items="domainOptions"
          item-title="title"
          item-value="value"
          v-model="selectedDomain"
          density="default"
          variant="outlined"
          color="primary"
          prepend-inner-icon="mdi-domain"
          hide-details
      />

      <div class="d-flex ga-4">
        <v-btn
            @click="filterDialogOpen = true"
            prepend-icon="mdi-filter-variant"
            elevation="2"
            variant="tonal"
            color="primary"
        >
          {{ t('personagrid.filterButton') }}
          <v-badge
              v-if="activeFilterCount"
              :content="activeFilterCount"
              color="error"
              inline
              class="ms-2"
          />
        </v-btn>
        <v-btn @click="showGeneratedPersonas" prepend-icon="mdi-creation" elevation="2" variant="flat" color="primary">
          {{isGeneratedView ? t('personagrid.hideGeneratedPersonas') : t('personagrid.viewGeneratedPersonas') }}
        </v-btn>
        <v-btn @click="toggleView" outlined>
          <v-icon v-if="isCarouselView">mdi-view-grid</v-icon>
          <v-icon v-else>mdi-view-carousel</v-icon>
          {{ isCarouselView ? t('personagrid.gridView') : t('personagrid.carouselView') }}
        </v-btn>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ t('personagrid.deletePersona') }}</v-card-title>
        <v-card-text>{{ t('personagrid.areYouSureYouWantToDeleteThisPersonaThisActionCannotBeUndone') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false"> {{ t('generic.cancel') }}</v-btn>
          <v-btn color="red" variant="tonal" @click="confirmDelete">{{ t('generic.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FilterSettingsDialog
        v-model="filterDialogOpen"
        :domain="selectedDomain"
        :personas="filterSourcePersonas"
        :is-generated="isGeneratedView"
        :applied-criteria="activeFilterCriteria"
        :schema-attributes="schemaAttributes"
        @apply="onApplyFilters"
        @clear="onClearFilters"
    />

    <v-alert
        v-if="showNoFilterResults"
        type="info"
        variant="tonal"
        class="mx-8 mb-4"
    >
      {{ t('personagrid.noPersonasMatchFilters') }}
    </v-alert>

    <v-alert
        v-if="isGeneratedView && generatedPersonas.length === 0"
        type="info"
        variant="tonal"
        class="mx-8 mb-4"
    >
      {{ t('personagrid.noGeneratedPersonas') }}
    </v-alert>

    <v-snackbar v-model="filterWarningSnackbar" color="warning" timeout="6000" location="bottom end">
      {{ filterWarningMessage }}
    </v-snackbar>

    <v-row v-if="!isCarouselView && isGeneratedView" class="mx-4">
      <v-col cols="12" md="4" v-for="persona in displayGeneratedPersonas" :key="persona.persona_id">
        <v-card
            class="persona-card d-flex flex-column"
            @click="goToPersonaInfo(persona, true)"
        >
          <v-card-item>
            <v-card-item-title class="text-h5 font-weight-bold mb-2">
              {{ savedPersonaDisplayName(persona) }}
            </v-card-item-title>
          </v-card-item>

          <v-card-item class="d-flex align-center">
            <v-avatar size="120" rounded="circle" class="me-4 mb-2">
              <v-img :src="getGeneratedPersonaImage(persona) || 'https://randomuser.me/api/portraits/lego/5.jpg'" cover :alt="$t('personagrid.profilePicture')"></v-img>
            </v-avatar>
            <v-chip-group column class="mb-2">
              <v-chip
                  v-if="savedPersonaField(persona, 'age') !== ''"
                  class="chip-tag ma-1"
                  label
                  outlined
                  size="small"
                  :style="getChipStyle('', 0)"
              >
                {{ t('personagrid.ageTag', { age: savedPersonaField(persona, 'age') }) }}
              </v-chip>
              <v-chip
                  v-if="savedPersonaField(persona, 'occupation') !== ''"
                  class="chip-tag ma-1"
                  label
                  outlined
                  size="small"
                  :style="getChipStyle('', 1)"
              >
                {{ t('personagrid.jobTag', { occupation: savedPersonaField(persona, 'occupation') }) }}
              </v-chip>
            </v-chip-group>
          </v-card-item>

          <v-card-text class="card-text-content flex-grow-1">
            <div v-if="savedPersonaField(persona, 'description') !== ''" class="mb-3">
              <strong class="text-black mb-2">{{ t('personagrid.description') }}</strong>
              <p>{{ savedPersonaField(persona, 'description') }}</p>
            </div>
            <div
                v-for="field in generatedPersonaDisplayFields(persona)"
                :key="field.key"
                class="info-row"
                :class="{ 'country-row d-flex': field.key === 'country' }"
            >
              <v-icon :icon="field.icon" size="small" class="info-icon"></v-icon>
              <span class="info-label label-bold">{{ field.label }}</span>
              <span class="info-value text-black">{{ field.value }}</span>
            </div>
            <div class="info-row last-info-row">
              <v-icon icon="mdi-calendar-clock-outline" size="small" class="info-icon"></v-icon>
              <span class="info-label">{{ t('personagrid.createdIn') }}</span>
              <span class="info-value">{{ savedPersonaCreatedAt(persona) }}</span>
              <v-btn
                  v-if="isAdmin"
                  @click.stop="openDeleteDialog(persona.persona_id)"
                  icon="mdi-delete-outline"
                  class="chat-button-inline"
                  variant="tonal"
                  color="red"
              ></v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Revised Carousel View -->
    <v-row v-if="isCarouselView && isGeneratedView" class="carousel-container mx-4">
      <v-carousel hide-delimiters height="auto" class="carousel-height">
        <template v-for="(persona, gen_index) in displayGeneratedPersonas">
          <v-carousel-item
              v-if="(gen_index) % columns === 0"
              :key="gen_index"
          >
            <v-row class="fill-height carousel-slide-row">
              <template v-for="col in columns">
                <template v-if="gen_index + col - 1 < displayGeneratedPersonas.length">
                  <v-col :key="col" class="carousel-card-col d-flex">
                    <v-card
                        class="persona-card d-flex flex-column"
                        @click="goToPersonaInfo(displayGeneratedPersonas[gen_index + col - 1], true)"
                    >
                      <v-card-item>
                        <v-card-item-title class="text-h5 font-weight-bold mb-2">
                          {{ savedPersonaDisplayName(displayGeneratedPersonas[gen_index + col - 1]) }}
                        </v-card-item-title>
                      </v-card-item>

                      <v-card-item class="d-flex align-center">
                        <v-avatar size="120" rounded="circle" class="me-4 mb-2">
                          <v-img :src="getGeneratedPersonaImage(displayGeneratedPersonas[gen_index + col - 1]) || 'https://randomuser.me/api/portraits/lego/5.jpg'" cover :alt="$t('personagrid.profilePicture')"></v-img>
                        </v-avatar>
                        <v-chip-group column class="mb-2">
                          <v-chip
                              v-if="savedPersonaField(displayGeneratedPersonas[gen_index + col - 1], 'age') !== ''"
                              class="chip-tag ma-1"
                              label
                              outlined
                              size="small"
                              :style="getChipStyle('', 0)"
                          >
                            {{ t('personagrid.ageTag', { age: savedPersonaField(displayGeneratedPersonas[gen_index + col - 1], 'age') }) }}
                          </v-chip>
                        </v-chip-group>
                      </v-card-item>

                      <v-card-text class="card-text-content flex-grow-1 mb-2">
                        <div
                            v-for="field in generatedPersonaDisplayFields(displayGeneratedPersonas[gen_index + col - 1])"
                            :key="field.key"
                            class="info-row"
                            :class="{ 'country-row d-flex': field.key === 'country' }"
                        >
                          <v-icon :icon="field.icon" size="small" class="info-icon"></v-icon>
                          <span class="info-label label-bold">{{ field.label }}</span>
                          <span class="info-value text-black">{{ field.value }}</span>
                        </div>
                        <div class="info-row last-info-row">
                          <v-icon icon="mdi-calendar-clock-outline" size="small" class="info-icon"></v-icon>
                          <span class="info-label">{{ t('personagrid.createdIn') }}</span>
                          <span class="info-value">{{ savedPersonaCreatedAt(displayGeneratedPersonas[gen_index + col - 1]) }}</span>
                          <v-btn
                              v-if="isAdmin"
                              @click.stop="openDeleteDialog(displayGeneratedPersonas[gen_index + col - 1].persona_id)"
                              icon="mdi-delete-outline"
                              class="chat-button-inline"
                              variant="tonal"
                              color="red"
                          ></v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </template>
              </template>
            </v-row>
          </v-carousel-item>
        </template>
      </v-carousel>
    </v-row>

    <v-row v-if="!isCarouselView && !isGeneratedView" class="mx-4">
      <v-col cols="12" md="4" v-for="(persona, index) in displayPersonas" :key="index">
        <v-card
            class="persona-card d-flex flex-column"
            @click="goToPersonaInfo(persona)"
        >
          <v-card-item>
            <v-card-item-title class="text-h5 font-weight-bold mb-2">
              {{ persona.title }}
            </v-card-item-title>
            <v-btn
                icon
                variant="plain"
                color="primary"
                class="ms-auto"
                @click.stop="openDialog(persona)"
            >
              <v-icon>mdi-information-circle-outline</v-icon>
            </v-btn>
          </v-card-item>

          <v-card-item class="d-flex align-center">
            <v-avatar size="120" rounded="circle" class="me-4 mb-2">
              <v-img :src="getPersonaImage(persona)" cover :alt="$t('personagrid.profilePicture')"></v-img>
            </v-avatar>
            <v-chip-group column class="mb-2">
              <v-chip
                  v-for="(tag, index) in persona.tags"
                  :key="index"
                  class="chip-tag ma-1"
                  label
                  outlined
                  size="small"
                  :style="getChipStyle(tag, index)"
              >
                {{ tag }}
              </v-chip>
            </v-chip-group>
            <v-card-text class="card-text-content mt-2">
              <strong class="text-black mb-2">{{ t('personagrid.description') }}</strong>{{ persona.description }}
            </v-card-text>
          </v-card-item>

          <v-card-text class="card-text-content flex-grow-1">
            <div class="info-row">
              <v-icon icon="mdi-currency-usd" size="small" class="info-icon"></v-icon>
              <span class="info-label label-bold">{{ t('personagrid.income') }}</span>
              <span class="info-value text-black">{{ safeCharacteristic(persona, 'income') }}</span>
            </div>
            <div class="info-row">
              <v-icon icon="mdi-tag-multiple" size="small" class="info-icon"></v-icon>
              <span class="info-label label-bold">{{ t('personagrid.interests') }}</span>
              <span class="info-value text-black">
                <template v-for="(interest, index) in persona.personal_interests">
                {{ interest }}{{ index < persona.personal_interests.length - 1 ? ', ' : '' }}
                </template>
              </span>
            </div>
            <template v-if="safeSegmentation(persona)">
              <div class="info-row">
                <v-icon icon="mdi-shape-outline" size="small" class="info-icon"></v-icon>
                <span class="info-label label-bold">{{ t('personagrid.segmentation') }}</span>
                <span class="info-value text-black">{{ safeSegmentation(persona) }}</span>
              </div>
            </template>
            <div class="info-row country-row d-flex">
              <v-icon icon="mdi-earth" size="small" class="info-icon"></v-icon>
              <span class="info-label label-bold">{{ t('personagrid.country') }}</span>
              <span class="info-value text-black">{{ safeCharacteristic(persona, 'country') }}</span>
              <div class="ml-2">
                <v-img
                    :src="getCountryFlag(getCountry(persona))"
                    :alt="$t('personagrid.countryFlag')"
                    class="rounded-flag"
                    width="25"
                ></v-img>
              </div>
            </div>
            <div class="info-row last-info-row">
              <v-icon icon="mdi-calendar-clock-outline" size="small" class="info-icon"></v-icon>
              <span class="info-label">{{ t('personagrid.createdIn') }}</span>
              <span class="info-value">{{ persona.created }}</span>
              <v-btn
                  icon
                  color="primary"
                  variant="tonal"
                  class="chat-button-inline"
                  @click.stop="newChat(persona)"
              >
                <v-icon>mdi-chat-processing-outline</v-icon>
              </v-btn>
            </div>
            <div class="d-flex mt-2">
              <v-icon
                  v-for="(icon, index) in persona.icons"
                  :key="index"
                  class="mr-2 text-grey-darken-2"
              >
                {{ icon }}
              </v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Revised Carousel View -->
    <div v-if="isCarouselView && !isGeneratedView" class="carousel-container mx-4">
      <v-carousel hide-delimiters height="auto" class="carousel-height">
        <template v-for="(persona, index) in displayPersonas">
          <v-carousel-item
              v-if="(index) % columns === 0"
              :key="index"
          >
            <v-row class="fill-height carousel-slide-row">
              <template v-for="n in columns">
                <template v-if="index + n - 1 < displayPersonas.length">
                  <v-col :key="n" class="carousel-card-col d-flex">
                    <v-card
                        class="persona-card-carousel d-flex flex-column"
                        @click="goToPersonaInfo(displayPersonas[index + n - 1])"
                    >
                      <v-card-item>
                        <v-card-item-title class="text-h5 font-weight-bold mb-2">
                          {{ displayPersonas[index + n - 1].title }}
                        </v-card-item-title>
                        <v-btn
                            icon
                            variant="plain"
                            color="primary"
                            class="ms-auto"
                            @click.stop="openDialog(displayPersonas[index + n - 1])"
                        >
                          <v-icon>mdi-information-circle-outline</v-icon>
                        </v-btn>
                      </v-card-item>

                      <v-card-item class="d-flex align-center">
                        <v-avatar size="120" rounded="circle" class="me-4 mb-2">
                          <v-img :src="getPersonaImage(displayPersonas[index + n - 1])" cover :alt="$t('personagrid.profilePicture')"></v-img>
                        </v-avatar>
                        <v-chip-group column class="mb-2">
                          <v-chip
                              v-for="(tag, tagIndex) in displayPersonas[index + n - 1].tags"
                              :key="tagIndex"
                              class="chip-tag ma-1"
                              label
                              outlined
                              size="small"
                              :style="getChipStyle(tag, tagIndex)"
                          >
                            {{ tag }}
                          </v-chip>
                        </v-chip-group>
                        <v-card-text class="card-text-content mt-2">
                          <strong class="text-black mb-2">{{ t('personagrid.description') }}</strong>
                          {{ displayPersonas[index + n - 1].description }}
                        </v-card-text>
                      </v-card-item>

                      <v-card-text class="card-text-content flex-grow-1">
                        <div class="info-row">
                          <v-icon icon="mdi-currency-usd" size="small" class="info-icon"></v-icon>
                          <span class="info-label label-bold">{{ t('personagrid.income') }}</span>
                          <span class="info-value text-black">{{
                              safeCharacteristic(displayPersonas[index + n - 1], 'income')
                            }}</span>
                        </div>
                        <div class="info-row">
                          <v-icon icon="mdi-tag-multiple" size="small" class="info-icon"></v-icon>
                          <span class="info-label label-bold">{{ t('personagrid.interests') }}</span>
                          <span class="info-value text-black">
                            <template v-for="(interest, interestIndex) in displayPersonas[index + n - 1].personal_interests">
                            {{ interest }}{{ interestIndex < displayPersonas[index + n - 1].personal_interests.length - 1 ? ', ' : '' }}
                            </template>
                          </span>
                        </div>
                        <template v-if="safeSegmentation(displayPersonas[index + n - 1])">
                          <div class="info-row">
                            <v-icon icon="mdi-shape-outline" size="small" class="info-icon"></v-icon>
                            <span class="info-label label-bold">{{t('personagrid.segmentation')}}</span>
                            <span class="info-value text-black">{{ safeSegmentation(displayPersonas[index + n - 1]) }}</span>
                          </div>
                        </template>
                        <div class="info-row country-row d-flex">
                          <v-icon icon="mdi-earth" size="small" class="info-icon"></v-icon>
                          <span class="info-label label-bold">{{ t('personagrid.country') }}</span>
                          <span class="info-value text-black">{{
                              safeCharacteristic(displayPersonas[index + n - 1], 'country')
                            }}</span>
                          <div class="ml-2">
                            <!-- src="@/assets/it_flag.svg" -->
                            <v-img
                                :src="getCountryFlag(getCountry(displayPersonas[index + n - 1]))"
                                :alt="$t('personagrid.countryFlag')"
                                class="rounded-flag"
                                width="25"
                            ></v-img>
                          </div>
                        </div>
                        <div class="info-row last-info-row">
                          <v-icon icon="mdi-calendar-clock-outline" size="small" class="info-icon"></v-icon>
                          <span class="info-label">{{ t('personagrid.createdIn') }}</span>
                          <span class="info-value">{{ displayPersonas[index + n - 1].created }}</span>
                          <v-btn
                              icon
                              color="primary"
                              variant="tonal"
                              class="chat-button-inline"
                              @click.stop="newChat(displayPersonas[index + n - 1])"
                          >
                            <v-icon>mdi-chat-processing-outline</v-icon>
                          </v-btn>
                        </div>
                        <div class="d-flex mt-2">
                          <v-icon
                              v-for="(icon, iconIndex) in displayPersonas[index + n - 1].icons"
                              :key="iconIndex"
                              class="mr-2 text-grey-darken-2"
                          >
                            {{ icon }}
                          </v-icon>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </template>
              </template>
            </v-row>
          </v-carousel-item>
        </template>
      </v-carousel>
    </div>
  </v-container>
</template>

<script>
import { delete_persona, build_domain_options, coerce_allowed_domain, get_default_domain, get_current_user_from_storage } from "@/api.js";
import { useI18n } from "vue-i18n";
import FilterSettingsDialog from "@/components/FilterSettingsDialog.vue";
import { filterPersonas, getAllFilterFields, hasActiveCriteria, reconcileFilterCriteria, formatDroppedFilterFieldsMessage } from "@/utils/personaFilter";
import {
  clearActiveFilterState,
  loadActiveFilterState,
  saveActiveFilterState,
} from "@/utils/filterStorage";
import {
  loadDomainPersonasWithOverlay,
  loadSavedPersonasForDomain,
  loadDomainSchemaAttributes,
  resolvePersonaImage,
  staticAssetPath,
  getSavedPersonaDisplayName,
  getSavedPersonaField,
  getSavedPersonaSchemaDisplayFields,
  formatSavedPersonaCreatedAt,
} from "@/utils/personaDomainLoader";

export default {
  components: {
    FilterSettingsDialog,
  },
  setup() {
		const { t } = useI18n();

		return {
			t
		};
	},
  data() {
    return {
      sidebar: false,
      selectedCard: null,
      personas: [],
      isCarouselView: false,
      columns: 3, // Define the number of columns for the carousel view
      selectedDomain: get_default_domain(),
      isGeneratedView: false,
      generatedPersonas: [],
      filterDialogOpen: false,
      activeFilterCriteria: [],
      deleteDialog: false,
      personaToDelete: null,
      filterWarningSnackbar: false,
      filterWarningMessage: '',
      schemaAttributes: [],
      domainReady: false,
      domainLoadSeq: 0,
    };
  },
  async mounted() {
    const fromRoute = coerce_allowed_domain(
      (this.$route.query.domain || '').toLowerCase() || null,
    );
    const domain = fromRoute || this.selectedDomain;
    if (domain) {
      this.selectedDomain = domain;
      // Only enter generated view when the URL explicitly requests it (?view=generated).
      // Do not restore isGeneratedView from localStorage — that caused a visible
      // flash from catalog → generated after the first paint.
      const wantGeneratedView = this.$route.query.view === 'generated';
      this.isGeneratedView = wantGeneratedView;
      await this.onDomainChange(domain, {
        clearFilters: false,
        syncRoute: false,
        generatedView: wantGeneratedView,
      });
    }
    this.domainReady = true;
    this.restoreActiveFilterState();
  },
  methods: {
    goToPersonaInfo(persona, generated = false) {
      const id = persona.persona_id || persona.id;
      sessionStorage.setItem("generated", JSON.stringify(generated));
      sessionStorage.setItem("personaData", JSON.stringify(persona))

      this.$router.push({
        name: "personaInfo",
        params: {id: id},
        query: {domain: this.selectedDomain}
      });
    },
    newChat(persona) {
      // Navigate to chat page and set the selectedPersona to persona.name
      console.log("Navigating to chat page with selected persona:", persona.name);
      this.$router.push({
        path: '/chat',
        query: {selectedPersona: persona.name, domain: this.selectedDomain}
      });
    },
    /**
     * Single entry point for domain changes (v-select, route query, mount).
     * Loads catalogue + generated lists for the target domain. Generated view
     * is kept when the user already toggled it on; initial page load only
     * enters generated view via ?view=generated (not localStorage).
     */
    async onDomainChange(domain, {
      clearFilters = true,
      syncRoute = true,
      generatedView = null,
    } = {}) {
      const allowed = coerce_allowed_domain(domain);
      if (!allowed) return;

      const loadSeq = ++this.domainLoadSeq;
      const preserveGeneratedView = this.isGeneratedView;

      // Mount/deep-link pass generatedView explicitly; domain switches preserve
      // the current mode when the user is already viewing generated personas.
      const nextGeneratedView = generatedView !== null
        ? generatedView
        : preserveGeneratedView;

      // The loaders never reject (they fall back to []), so fetch in parallel
      // and commit everything below in one tick — no half-switched render.
      const [personas, schemaAttributes, generatedPersonas] = await Promise.all([
        loadDomainPersonasWithOverlay(allowed, this.t),
        loadDomainSchemaAttributes(allowed),
        loadSavedPersonasForDomain(allowed),
      ]);
      // A newer domain change started meanwhile: drop these stale results
      // instead of letting them overwrite the newer domain's data.
      if (loadSeq !== this.domainLoadSeq) return;

      // Snapshot each persona's just-computed tags (age/job translated
      // strings, or the static per-domain fallback text when age/occupation
      // are absent — see overlayBackendData in personaDomainLoader.js) so
      // refreshLocalizedTags() can rebuild them on a later locale switch
      // without needing to touch that shared loader.
      personas.forEach((persona) => {
        if (Array.isArray(persona.tags)) {
          persona._tagFallback = [...persona.tags];
        }
      });

      // Cleared here rather than before loading, so an active filter does not
      // briefly expand the old domain's list while the new one is fetched.
      if (clearFilters) {
        this.onClearFilters();
      }
      this.personas = personas;
      this.schemaAttributes = schemaAttributes;
      this.generatedPersonas = generatedPersonas;
      this.isGeneratedView = nextGeneratedView;
      if (syncRoute) {
        this.updateRouteWithDomain(allowed);
      }
    },
    // Persona Schema for the filter panel (Option 2: schema-driven,
    // type-aware custom attribute filters). Failure/empty schema is
    // handled gracefully — schemaAttributes stays [] and
    // getAllFilterFields() falls back to its existing data-driven
    // discovery, exactly as before this feature existed.
    async loadSchemaAttributes(domain) {
      try {
        const response = await fetch_persona_domain_schema(domain);
        this.schemaAttributes = response.data?.attributes || [];
      } catch (error) {
        console.error('Failed to load persona domain schema for filters:', error);
        this.schemaAttributes = [];
      }
    },
    // Re-derives Age/Job chip text for every already-loaded persona using
    // the current locale. Mirrors overlayBackendData()'s own age/occupation
    // logic exactly, using the _tagFallback snapshot in place of the
    // original staticPersona.tags — age/occupation availability never
    // changes between locale switches, so a persona that fell back to the
    // static tag at load time takes the same fallback branch here too,
    // leaving that (non-translated) text unchanged.
    refreshLocalizedTags() {
      this.personas.forEach((persona) => {
        const fallback = persona._tagFallback;
        if (!fallback) return;
        const pc = persona.personal_characteristics || {};
        const age = pc.age;
        const occupation = pc.occupation;
        persona.tags = [
          age !== undefined && age !== null ? this.t('personagrid.ageTag', { age }) : fallback[0],
          occupation ? this.t('personagrid.jobTag', { occupation }) : fallback[1],
        ].filter(Boolean);
      });
    },
    updateRouteWithDomain(domain) {
      const q = { ...(this.$route.query || {}), domain };
      if (this.isGeneratedView) {
        q.view = 'generated';
      } else {
        delete q.view;
      }
      this.$router.replace({ name: 'persona', query: q });
    },
    formatKey(key) {
      const spaced = key.replace(/_/g, ' ');
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    },
    getChipStyle(tag, index) {
      if (index === 0) {
        return {backgroundColor: 'MediumSlateBlue', color: 'black'};
      } else if (index === 1) {
        return {backgroundColor: 'orange', color: 'black'};
      } else {
        return {backgroundColor: 'orange', color: 'black'};
      }
    },
    toggleView() {
      this.isCarouselView = !this.isCarouselView;
    },
    getPersonaImage(persona) {
      return resolvePersonaImage(persona);
    },
    // Safely access a characteristic across different schemas
    safeSegmentation(persona) {
      return persona?.personal_characteristics?.segmentation || persona?.segmentation || '';
    },
    safeCharacteristic(p, key) {
      return (p?.personal_characteristics?.[key])
          || (p?.personalCharacteristics?.[key])
          || p?.[key]
          || '';
    },
    getCountry(p) {
      return this.safeCharacteristic(p, 'country') || this.safeCharacteristic(p, 'residence') || (p?.country || '');
    },
    normalizePersona(p) {
      if (!p) return p;
      const n = {...p};
      n.title = n.title || n.name;
      n.personal_characteristics = {
        ...(n.personal_characteristics || {}),
        ...(n.personalCharacteristics || {}),
      };
      if (!n.personal_characteristics.country && n.country) {
        n.personal_characteristics.country = n.country;
      }
      n.personal_interests = n.personal_interests || n.personalInterests || [];
      n.devices = Array.isArray(n.devices) ? n.devices : (n.currentTechnologyAndTools || []);
      n.created = n.created || new Date().getFullYear().toString();
      return n;
    },
    getCountryFlag(country) {
      // Assuming your flag SVG files are located in `@/assets/flags/`
      // and are named with the lowercase country name (e.g., italy.svg, usa.svg)
      return staticAssetPath(`/imgs/flags/${country.toLowerCase().replace(/ /g, '_')}.png`);
    },
    openSidebar(persona) {
      this.selectedCard = persona;
      this.sidebar = true;
    },
    openDialog(persona) {
      // This method was called in the template but wasn't defined in the original code
      // I'm adding a placeholder implementation
      this.selectedCard = persona;
      // Add dialog opening logic here if needed
    },
    showGeneratedPersonas() {
      this.toggleGeneratedView(!this.isGeneratedView);
    },
    async toggleGeneratedView(nextView) {
      if (nextView) {
        await this.retrieveGeneratedPersonas();
        this.onClearFilters();
      }
      this.isGeneratedView = nextView;
      saveActiveFilterState(this.selectedDomain, {
        criteria: this.activeFilterCriteria,
        isGeneratedView: this.isGeneratedView,
      });
      this.updateRouteWithDomain(this.selectedDomain);
    },
    async retrieveGeneratedPersonas(domain = this.selectedDomain) {
      this.generatedPersonas = await loadSavedPersonasForDomain(domain);
    },
    savedPersonaDisplayName(persona) {
      return getSavedPersonaDisplayName(persona, this.t('generatepersonas.unnamedPersona'));
    },
    savedPersonaField(persona, key) {
      return getSavedPersonaField(persona, key);
    },
    generatedPersonaDisplayFields(persona) {
      return getSavedPersonaSchemaDisplayFields(persona, this.schemaAttributes);
    },
    savedPersonaCreatedAt(persona) {
      return formatSavedPersonaCreatedAt(persona, this.$i18n.locale);
    },
    // retrieveGeneratedPersonas() transforms persona.data into an array of
    // {key, value} pairs — look up the 'image' entry saved via Edit
    // Persona's "Change Image" (if any) instead of the never-populated
    // top-level persona.image.
    getGeneratedPersonaImage(persona) {
      const entry = Array.isArray(persona?.data) ? persona.data.find((item) => item.key === 'image') : null;
      return entry ? staticAssetPath(entry.value) : '';
    },
    openDeleteDialog(persona_id) {
      this.personaToDelete = persona_id;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteDialog = false;
      await this.deletePersona(this.personaToDelete);
      this.personaToDelete = null;
    },
    async deletePersona(persona_id) {
      try {
        const response = await delete_persona(persona_id);
        console.log(response);
        await this.retrieveGeneratedPersonas();
        await this.onDomainChange(this.selectedDomain);
      } catch (error) {
        console.log(error);
      }
    },
    onApplyFilters(criteria) {
      this.activeFilterCriteria = criteria;
      saveActiveFilterState(this.selectedDomain, {
        criteria,
        isGeneratedView: this.isGeneratedView,
      });
    },
    onClearFilters() {
      this.activeFilterCriteria = [];
      clearActiveFilterState(this.selectedDomain);
    },
    showFilterDroppedWarning(droppedFields) {
      if (!droppedFields?.length) return;
      this.filterWarningMessage = formatDroppedFilterFieldsMessage(droppedFields, this.t);
      this.filterWarningSnackbar = true;
    },
    restoreActiveFilterState() {
      const saved = loadActiveFilterState(this.selectedDomain);
      if (!saved) return;

      const source = this.isGeneratedView ? this.generatedPersonas : this.personas;
      const fields = getAllFilterFields(source, this.isGeneratedView, this.schemaAttributes);
      const { criteria, droppedFields } = reconcileFilterCriteria(
        saved.criteria || [],
        fields,
        this.schemaAttributes,
      );
      this.activeFilterCriteria = criteria;
      this.showFilterDroppedWarning(droppedFields);

      if (droppedFields.length) {
        saveActiveFilterState(this.selectedDomain, {
          criteria,
          isGeneratedView: this.isGeneratedView,
        });
      }
    },
  },
  computed: {
    domainOptions() {
      return build_domain_options(this.$t);
    },
    filterSourcePersonas() {
      return this.isGeneratedView ? this.generatedPersonas : this.personas;
    },
    displayPersonas() {
      return filterPersonas(
        this.personas,
        this.activeFilterCriteria,
        false,
        this.currentFilterFields,
      );
    },
    displayGeneratedPersonas() {
      return filterPersonas(
        this.generatedPersonas,
        this.activeFilterCriteria,
        true,
        this.currentFilterFields,
      );
    },
    currentFilterFields() {
      return getAllFilterFields(this.filterSourcePersonas, this.isGeneratedView, this.schemaAttributes);
    },
    activeFilterCount() {
      return this.activeFilterCriteria.length;
    },
    showNoFilterResults() {
      if (!hasActiveCriteria(this.activeFilterCriteria, this.currentFilterFields)) {
        return false;
      }

      if (this.isGeneratedView) {
        return this.generatedPersonas.length > 0 && this.displayGeneratedPersonas.length === 0;
      }

      return this.personas.length > 0 && this.displayPersonas.length === 0;
    },
    // Delete is admin-only on the backend (2Q-02); hide the control for
    // non-admins instead of letting the click silently 403.
    isAdmin() {
      const user = get_current_user_from_storage();
      return user?.role_id === 'admin' || user?.role_id === 'super_admin';
    },
  },
  watch: {
    // Age/Job tags are plain strings baked in at load time (see onDomainChange
    // above) — vue-i18n's own reactivity can't reach back into them, so the
    // locale switch has to explicitly trigger a recompute.
    '$i18n.locale'() {
      this.refreshLocalizedTags();
    },
    selectedDomain: {
      async handler(domain, previous) {
        if (!this.domainReady || !domain || domain === previous) return;
        await this.onDomainChange(domain);
      },
    },
    '$route.query.domain': {
      handler(rawDomain) {
        if (this.$route.name !== 'persona') return;
        const domain = coerce_allowed_domain((rawDomain || '').toLowerCase() || null);
        if (!domain || domain === this.selectedDomain) return;
        this.selectedDomain = domain;
      },
    },
    '$route.query.view': {
      immediate: false,
      async handler(view) {
        if (!this.selectedDomain) return;
        if (view === 'generated' && !this.isGeneratedView) {
          await this.toggleGeneratedView(true);
          return;
        }
        if (view !== 'generated' && this.isGeneratedView) {
          this.isGeneratedView = false;
          saveActiveFilterState(this.selectedDomain, {
            criteria: this.activeFilterCriteria,
            isGeneratedView: false,
          });
          this.updateRouteWithDomain(this.selectedDomain);
        }
      },
    },
  },
};
</script>

<style>
.domain-select :deep(.v-field) {
  border-width: 2px;
  border-color: rgb(var(--v-theme-primary));
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.domain-select :deep(.v-field__input) {
  font-size: 1.05rem;
  font-weight: 600;
}

.domain-select :deep(.v-label) {
  font-weight: 700;
  opacity: 0.95;
}
</style>

<style scoped>
/* Domain selector */
.domain-select {
  max-width: 420px;
}

.domain-select :deep(.v-field__input) {
  font-size: 1.05rem;
  font-weight: 600;
  padding-top: 14px;
  padding-bottom: 14px;
}

.persona-card {
  transition: transform 0.15s ease-in-out;
  cursor: pointer;
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100%; /* Ensure cards fill the row height */
}

.persona-card:hover {
  transform: scale(1.03);
}

/* Specific styles for carousel cards */
.carousel-container {
  position: relative;
  overflow: hidden;
}

.carousel-height {
  min-height: 750px; /* Set a minimum height for the carousel to accommodate all content */
}

.carousel-slide-row {
  padding: 16px;
  align-items: stretch;
}

.carousel-card-col {
  height: 100%;
  padding: 8px;
}

.persona-card-carousel {
  cursor: pointer;
  display: grid;
  grid-template-rows: auto 1fr auto;
  margin: 0 8px;
  /* Add a transform-origin to ensure the card scales from the center */
  transform-origin: center center;

  flex: 1;
  height: auto;
  /* Ensure fixed sizing for carousel cards */
  max-height: 700px;
  overflow-y: hidden;
  /* Smooth transition */
  transition: all 0.2s ease;

}

.persona-card-carousel:hover {
  transform: scale(1.03);
  /* Ensure z-index is higher so the card appears above others during hover */
  z-index: 1;
  /* The overflow remains hidden so the content doesn't spill out */
}

.chip-tag {
  border-radius: 8px; /* Slightly more rounded chips */
}

.card-text-content {
  padding-bottom: 20px; /* Make space for inline chat button */
  position: relative; /* Needed for absolute positioning of chat button if desired otherwise flexbox approach is used*/
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  align-items: left; /* Vertically center icon and text */
  margin-bottom: 4px; /* Add some spacing between info rows */
  line-height: 1.2;
}

.info-icon {
  margin-right: 8px;
  color: rgba(0, 0, 0, 0.6); /* Muted icon color */
}

.info-label {
  font-size: 0.9rem; /* Slightly smaller label text */
  color: rgba(0, 0, 0, 0.7); /* Muted label color */
  margin-right: 4px; /* Spacing after label */
}

.info-value {
  font-size: 0.9rem; /* Slightly smaller value text to match labels */
  color: rgba(0, 0, 0, 0.9); /* Slightly darker value text for emphasis */
  font-weight: 500; /* Medium font weight for values */
}

.last-info-row {
  display: flex; /* Use flexbox for the last info row */
  align-items: center; /* Vertically align items in the last row */
  justify-content: space-between; /* Distribute space between the text and the button if needed */
}

.chat-button-inline {
  margin-left: auto; /* Push chat button to the right end of the last info row */
}

.v-card-subtitle {
  white-space: normal; /* Ensure text wraps */
  word-wrap: break-word; /* Break long words if necessary */
  overflow-wrap: break-word; /* Another way to handle breaking long words */
  word-break: break-word; /* Force word break if necessary */
}

.v-avatar {
  border: 3px solid #e0e0e0;
}

.rounded-flag {
  border-radius: 4px;
}

.country-row {
  align-items: left; /* Align flag in country row to center */
}

.label-bold {
  font-weight: bold; /* Make labels bold */
  color: black; /* Make labels black */
}

.fill-height {
  height: 100%;
}

/* Ensure all cards in a row have the same height */
.v-row {
  display: flex;
  flex-wrap: wrap;
}

.v-col {
  display: flex;
}

</style>

